import React from 'react';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { alpha, Box, Card, CardContent, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { calculateDeltaPercent } from '../dashboardAnalytics.utils';

type ProfileVisitorsSummaryCardProps = {
    profileVisitorsLoadedCount: number;
    maxCount: number;
    showComparison: boolean;
    display: { profileVisitorsTotal: number; profileVisitorsGuests: number; profileVisitorsUsers: number; profileVisitorsSubscribers: number };
    comparisonProfileVisitorsTotals: { total: number; guests: number; users: number; subscribers: number };
    hasData: boolean;
};

const ProfileVisitorsSummaryCard = React.memo((props: ProfileVisitorsSummaryCardProps) => {
    const { profileVisitorsLoadedCount, maxCount, showComparison, display, comparisonProfileVisitorsTotals, hasData } = props;

    if (profileVisitorsLoadedCount < maxCount) return <Skeleton height={120} />;
    if (!hasData) return <Typography color="text.secondary">No visitor data available for selected subscribers.</Typography>;

    const items = [
        { icon: <GroupIcon color="primary" />, title: 'Total Visitors', value: display.profileVisitorsTotal, previous: comparisonProfileVisitorsTotals.total },
        { icon: <PersonOffIcon color="error" />, title: 'Guest Visitors', value: display.profileVisitorsGuests, previous: comparisonProfileVisitorsTotals.guests },
        { icon: <PersonIcon color="info" />, title: 'User Visitors', value: display.profileVisitorsUsers, previous: comparisonProfileVisitorsTotals.users },
        { icon: <StarBorderIcon color="secondary" />, title: 'Subscriber Visitors', value: display.profileVisitorsSubscribers, previous: comparisonProfileVisitorsTotals.subscribers },
    ];

    return (
        <Grid container spacing={1}>
            {items.map((item) => {
                const deltaPercent = calculateDeltaPercent(item.value, item.previous);
                const isPositive = (deltaPercent ?? 0) >= 0;
                return (
                    <Grid item xs={12} sm={6} md={6} lg={3} key={item.title}>
                        <Card variant="outlined" sx={{ borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Stack direction="row" spacing={1.25} alignItems="center">
                                    <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: (t) => alpha(t.palette.primary.main, 0.08), border: '1px solid', borderColor: (t) => alpha(t.palette.primary.main, 0.18), display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {item.icon}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="subtitle2" color="text.secondary">{item.title}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums', fontSize: { xs: '1.1rem', md: '1.5rem' } }}>
                                            {item.value.toLocaleString()}
                                        </Typography>
                                        {showComparison ? (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.35 }}>
                                                <Typography variant="body2" sx={{ color: isPositive ? 'success.main' : 'error.main', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                                                    {deltaPercent === null ? 'N/A' : isPositive ? '↑' : '↓'} {deltaPercent !== null ? `${Math.abs(deltaPercent).toFixed(1)}%` : ''}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                    vs previous period
                                                </Typography>
                                            </Stack>
                                        ) : null}
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
});

ProfileVisitorsSummaryCard.displayName = 'ProfileVisitorsSummaryCard';
export default ProfileVisitorsSummaryCard;
